/* eslint-disable react/display-name */
// react
import React, { useEffect, useRef, useState } from 'react'

// components
import { Dialog3 as Dialog } from '@st-operations/uikit/src/v1/index.js'
import { Button, BUTTON_SIZE, BUTTON_THEME, Icon } from '@st-operations/uikit/src/v2/index.js'
import ItemInfoCell from '_components/item-info-cell'
import Header from '_components/header'
import MoreInfoDropdown from '_components/more-info-dropdown'
import NewEntityDialog from '_components/new-audience-dialog'
import ListView from '_components/list-view'
import ModalCloneAudience from '_components/custom-audiences/modal-clone-audience'
import ModalMergeAudience from '_components/custom-audiences/modal-merge-audience'
import BatchSidebar, { ACTIONS } from '_components/batch-sidebar'
import ModalBulkEditAudience from '_components/custom-audiences/modal-bulk-edit-audience'

// lib
import Events from '@st-operations/uikit/Events.js'
import Storage from '@st-operations/uikit/Storage.js'
import format from '@st-operations/uikit/lib/format.js'
import pretty from '@st-operations/uikit/lib/pretty.js'
import bulkUpdate from '_lib/bulkUpdate.js'

// css
import '../../../css/pages/CustomAudiences.styl'

// services
import { mergeAudiences, getCustomAudienceSchema } from '_services/audience'
import { getExpiringReport } from '../audience-creator/utils'

export const STATUSES = ['draft', 'approved', 'archived', 'processed', 'deleted']

// CustomAudiences Page
const CustomAudiencesPage = (props) => {
  const {
    location: { search },
  } = props
  const batch_prebuilt_attribute_process_id = new URLSearchParams(search).get(
    'batch_prebuilt_attribute_process_id'
  )
  const [state, _setState] = useState({
    batch_percentage: 0,
    batch_size: 0,
    selectedAudiences: [],
    created_by: [],
    destinations: [],
    schema: {},
    error: null,
    verticals: [],
    show_info: false,
    isModalCreateCustomAudienceOpen: false,
    isModalCloneOpen: false,
    isModalMergeOpen: false,
    remountKey: 0,
  })

  const setState = (newState) => _setState((prevState) => ({ ...prevState, ...newState }))

  const softRefresh = (error) => !error && setState({ remountKey: Math.random() })

  const listViewItemsRef = useRef([])

  useEffect(() => {
    loadCreatedBy()
    loadDestinations()
    loadVerticals()
    loadSchema()
  }, [])

  const toggleModal = () => {
    _setState((prevState) => ({
      ...prevState,
      isModalCreateCustomAudienceOpen: !prevState.isModalCreateCustomAudienceOpen,
    }))
  }

  const loadCreatedBy = () => {
    window._api.get('/custom-audiences/enum/created_by.email', (error, items) => {
      setState({ error, created_by: items })
    })
  }

  const loadDestinations = () => {
    window._api.get('/destinations', (error, resp) => {
      // filter test and 4info
      let destinations = resp.items
      destinations = destinations.filter((item) => {
        return item.name !== '4Info' && !item.name.toLowerCase().includes('test')
      })
      setState({ error, destinations })
    })
  }

  const loadVerticals = () => {
    window._api.get('/custom-audiences/enum/vertical', (error, items) => {
      const verticals = items.filter(Boolean)
      setState({ error, verticals })
    })
  }

  const loadSchema = () => {
    getCustomAudienceSchema()
      .then((schema) => setState({ schema }))
      .catch((error) => setState({ error }))
  }

  const approve = (items) => {
    const ids = items.map(({ _id }) => _id)
    startBatchProcess(ids)
    let bulkAction = {
      body: { status: 'approved' },
      ids,
      method: 'put',
      url: '/custom-audiences',
    }
    bulkUpdate(bulkAction, updateBatchProgress, softRefresh)
  }

  const archive = (items) => {
    const ids = items.map(({ _id }) => _id)
    startBatchProcess(ids)
    let bulkAction = {
      body: { status: 'archived' },
      ids,
      method: 'put',
      url: '/custom-audiences',
    }
    bulkUpdate(bulkAction, updateBatchProgress, softRefresh)
  }

  const openModalClone = (items) => {
    setState({
      isModalCloneOpen: true,
      selectedAudiences: items,
    })
  }

  const openModalMerge = (items) => {
    setState({
      isModalMergeOpen: true,
      selectedAudiences: items,
    })
  }

  const openModalBulkEdit = (items) => {
    setState({
      isModalBulkEditOpen: true,
      selectedAudiences: items,
    })
  }

  const merge = (payload) => {
    mergeAudiences(payload)
      .then((response) => {
        const id = response?.insertedIds?.[0]
        id && _history.push(`/audience-creator/${id}`)
      })
      .catch((error) => toast.error('Error merging audience.'))
  }

  const cloneAudienceCreator = (audiences) => {
    const ids = Object.keys(audiences)
    startBatchProcess(ids)
    let bulkAction = {
      body: {
        require_unique_name: 'false',
      },
      ids,
      audiences,
      oldFields: state.selectedAudiences,
      method: 'post',
      url: `/custom-audiences/clone`,
    }
    bulkUpdate(bulkAction, updateBatchProgress, softRefresh)
  }

  const onBulkEdit = (audiences) => {
    const ids = Object.keys(audiences)
    startBatchProcess(ids)
    let bulkAction = {
      ids,
      audiences,
      oldFields: state.selectedAudiences,
      method: 'put',
      url: `/custom-audiences`,
    }
    bulkUpdate(bulkAction, updateBatchProgress, softRefresh)
  }

  const deleteAudience = (items) => {
    const ids = items.map(({ _id }) => _id)
    startBatchProcess(ids)
    let bulkAction = {
      body: { status: 'deleted' },
      ids,
      method: 'put',
      url: `/custom-audiences`,
    }
    bulkUpdate(bulkAction, updateBatchProgress, softRefresh)
  }

  const startBatchProcess = (ids) => {
    Storage.set('abort_batch', false)
    Events.emit('set_batch_process', true)
    setState({ batch_percentage: 0, batch_size: ids.length ?? ids.size })
  }

  const updateBatchProgress = () => {
    _setState((prevState) => ({
      ...prevState,
      batch_percentage: prevState.batch_percentage + 1 / prevState.batch_size,
    }))
  }

  const handleCloseInfo = () => {
    setState({ show_info: false })
  }

  const handleSelectionChange = ({ action, items }) => {
    const selectedEntities = listViewItemsRef.current.filter(({ _id }) => items.includes(_id))
    Events.emit('ListView-selection', { action, items, entities: selectedEntities })
  }

  const handleBatchSidebarAction = (action, items) => {
    switch (action) {
      case ACTIONS.APPROVE:
        return approve(items)
      case ACTIONS.ARCHIVE:
        return archive(items)
      // case ACTIONS.BATCH_PREBUILT_ATTRIBUTE_SETUP:
      //   return window._history.push(
      //     `/batch-prebuilt-attributes/setup?audience_template_id=${items[0]._id}&name=${items[0].name}`
      //   )
      case ACTIONS.CLONE:
        return openModalClone(items)
      case ACTIONS.DELETE:
        return deleteAudience(items)
      case ACTIONS.MERGE:
        return openModalMerge(items)
      case ACTIONS.BULK_EDIT:
        return openModalBulkEdit(items)
    }
  }

  return (
    <div id="custom-audiences-page" key={state.remountKey}>
      <Header
        title="Audience Creator"
        className="header__container"
        actions={[
          {
            children: 'Download Expiring Report',
            onClick: getExpiringReport,
            theme: BUTTON_THEME.SECONDARY,
          },
          {
            children: 'Create New Audience',
            onClick: toggleModal,
          },
          {
            theme: BUTTON_THEME.GHOST,
            children: (
              <Icon
                type="outline"
                icon="info"
                color="#13682B"
                className="header__button"
                width="24px"
                height="24px"
              />
            ),
            onClick: () =>
              _setState((prevState) => ({ ...prevState, show_info: !prevState.show_info })),
          },
        ]}
      />
      <If condition={state.show_info}>
        <MoreInfoDropdown onClose={handleCloseInfo} />
      </If>

      <If condition={state.error}>
        <p>{state.error.message}</p>
      </If>

      <div className="batch-sidebar__list-view-wrapper">
        <ListView
          className="audience__builder st-list-view--has-sidebar"
          inputClassName="search__input"
          showCheckboxes={true}
          onSelectionChange={handleSelectionChange}
          batch_percentage={state.batch_percentage}
          batch_size={state.batch_size}
          itemsRef={listViewItemsRef}
          columns={[
            {
              key: 'name',
              name: 'Name',
              render: (item) => {
                const audienceNameColFields = [
                  {
                    root: 'destinations',
                    props: ['name', 'external_id'],
                  },
                ]
                if (item.openx_deal_id) audienceNameColFields.push('openx_deal_id')
                return (
                  <ItemInfoCell
                    fields={audienceNameColFields}
                    actions={(item) => (
                      <div className="st-item-info-cell__actions">
                        <Button
                          className="st-item-info-cell__action-button"
                          theme={BUTTON_THEME.SECONDARY}
                          size={BUTTON_SIZE.SMALLEST}
                          link={`/audience/${item._id}/insights`}
                        >
                          Open in Audience Insights
                          <Icon width="16px" height="16px" icon="externallink" type="outline" />
                        </Button>
                        <Button
                          className="st-item-info-cell__action-button"
                          theme={BUTTON_THEME.SECONDARY}
                          size={BUTTON_SIZE.SMALLEST}
                          link={`/data-explorer/discovery?audience_id=${item._id}&audience_name=${item.name}`}
                        >
                          Open in Data Explorer
                          <Icon width="16px" height="16px" icon="externallink" type="outline" />
                        </Button>
                      </div>
                    )}
                    className="custom__info__cell"
                    suffixClassName="info__cell__suffix"
                    item={item}
                    key={`item-${item.index}`}
                    link={`/audience-creator/${item._id}`}
                    nameSingleLine
                  />
                )
              },
              sortable: true,
              styles: { paddingRight: '40px' },
            },
            {
              name: 'Status',
              key: 'status',
              styles: { paddingRight: '40px', maxWidth: '128px' },
              render: (item) => pretty(item.status),
            },
            {
              name: 'Size',
              key: 'actual_size',
              styles: { paddingRight: '40px', maxWidth: '130px' },
              render: (item) => {
                if (item.destination_sizes) {
                  const slugs = item.destinations.map(({ slug }) => slug)
                  return (
                    <div className="size-cell">
                      {item.destinations.map(({ slug, name, label }) => (
                        <>
                          {name || label}: {format('metric', item.destination_sizes[slug])}
                          <br />
                        </>
                      ))}
                      Total:{' '}
                      {format(
                        'metric',
                        Object.entries(item.destination_sizes)
                          .filter(([key]) => slugs.includes(key))
                          .reduce((sum, [_, item]) => sum + item, 0)
                      )}
                    </div>
                  )
                }
                return (
                  <div className="size-cell" style={{ textAlign: 'center' }}>
                    {format('metric', item.actual_size)}
                  </div>
                )
              },
            },
            {
              key: 'created_by.email',
              name: 'Created By',
              styles: { paddingRight: '40px', maxWidth: '240px', boxSizing: 'border-box' },
              render: (item) => (
                <div className="created-by-cell">
                  <span>{item.created_by.email}</span> <br />
                  <span className="created-by-cell__last-edited">
                    Last edited by: {item.updated_by.email}
                  </span>
                </div>
              ),
            },
            {
              format: 'date',
              key: 'updated_at',
              name: 'Last Updated',
              sortable: true,
              styles: { maxWidth: '100px' },
            },
          ]}
          entities="custom-audiences"
          filters={[
            {
              name: 'created_by.email',
              label: 'Created by',
              options: state.created_by,
              all: true,
              className: 'audiences__filters',
            },
            {
              name: 'destinations',
              label: 'Destination',
              options: state.destinations,
              all: true,
            },
            { name: 'status', options: STATUSES, all: true },
            {
              name: 'vertical',
              label: 'Vertical',
              options: state.verticals,
              all: true,
              pretty: false,
            },
            batch_prebuilt_attribute_process_id && {
              name: 'batch_prebuilt_attribute_process_id',
              label: 'Batch ID',
              options: [batch_prebuilt_attribute_process_id],
              all: true,
              pretty: false,
            },
          ].filter(Boolean)}
          filter_on_load={
            batch_prebuilt_attribute_process_id
              ? { batch_prebuilt_attribute_process_id }
              : { status: 'approved' }
          }
          persistentFiltersOnRefresh={!batch_prebuilt_attribute_process_id}
          searchable={true}
          sort_key={'updated_at'}
          sort_dir={-1}
          stateful
        />
        <BatchSidebar
          showGeoAudienceCheck={true}
          onConfirm={handleBatchSidebarAction}
          actions={[
            ACTIONS.APPROVE,
            ACTIONS.ARCHIVE,
            // ACTIONS.BATCH_PREBUILT_ATTRIBUTE_SETUP,
            ACTIONS.CLONE,
            ACTIONS.DELETE,
            ACTIONS.BULK_EDIT,
            ACTIONS.MERGE,
          ]}
        />
      </div>

      <ModalCloneAudience
        selectedAudiences={state.selectedAudiences}
        isModalCloneOpen={state.isModalCloneOpen}
        clone={cloneAudienceCreator}
        closeModal={() => setState({ isModalCloneOpen: false })}
      />

      <ModalBulkEditAudience
        audiences={state.selectedAudiences}
        isOpen={state.isModalBulkEditOpen}
        onSubmit={onBulkEdit}
        onClose={() => setState({ isModalBulkEditOpen: false })}
        schema={state.schema}
      />

      <ModalMergeAudience
        selectedAudiences={state.selectedAudiences}
        isModalMergeOpen={state.isModalMergeOpen}
        merge={merge}
        closeModal={() => setState({ isModalMergeOpen: false })}
      />

      <Dialog open={state.isModalCreateCustomAudienceOpen} close={toggleModal}>
        <NewEntityDialog
          close={toggleModal}
          entities="custom-audiences"
          open={state.isModalCreateCustomAudienceOpen}
          title="Create Custom Audience"
          value={'ShareThis > Interest from Social Activity > UPDATE_ME'}
          urlRedirect="audience-creator"
          defaultProperties={{
            destinations: [
              {
                _id: '632dec466420cb001a723e60',
                name: 'LiveRamp US 2',
                slug: 'liveramp-us-2',
              },
            ],
          }}
        />
      </Dialog>
    </div>
  )
}

// export
export default CustomAudiencesPage
